// Brief Prospection Phase 1 §1 — Page profil public /@{handle}. Aucune auth requise.
// Brief 27 oct B.3 — refonte : CTA de conversion clairs (réserver / suivre /
// créer un compte), partage ouvert à tous les visiteurs, variante non-expert
// (compétences + dernières publications) au lieu d'un bloc « sessions » vide.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Star, Users, CalendarCheck, Clock, ArrowRight, Share2, MapPin, MessageCircle, Heart, UserPlus, CalendarDays, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SharePanel from '../components/SharePanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = '#7F77DD';

const fmtDay = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return { day: '', month: '' };
  return {
    day: d.toLocaleDateString('fr-CH', { day: 'numeric' }),
    month: d.toLocaleDateString('fr-CH', { month: 'short' }).replace('.', ''),
  };
};

const fmtTime = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('fr-CH', { weekday: 'long' }) + ' · ' +
    d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Stars = ({ value = 0 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`h-4 w-4 ${n <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
    ))}
  </span>
);

const PrimaryBtn = ({ onClick, children, testId, className = '' }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md hover:opacity-90 active:scale-[0.98] transition-all ${className}`}
    style={{ backgroundColor: ACCENT }}
    data-testid={testId}
  >
    {children}
  </button>
);

const GhostBtn = ({ onClick, children, testId, className = '' }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:border-[#7F77DD]/60 hover:text-[#7F77DD] transition-colors ${className}`}
    data-testid={testId}
  >
    {children}
  </button>
);

export default function PublicExpertPage() {
  const { handle: rawHandle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const handle = (rawHandle || '').replace(/^@/, '');
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const trackedRef = React.useRef(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/public/profile/${handle}`)
      .then((r) => {
        if (!alive) return;
        setProfile(r.data);
        document.title = r.data.is_expert
          ? `${r.data.display_name} — sessions & expertise`
          : `${r.data.display_name} sur OpenSapiens`;
        if (!trackedRef.current) {
          trackedRef.current = true;
          const ref = new URLSearchParams(window.location.search).get('ref');
          if (ref) sessionStorage.setItem('os_ref', ref);
          axios.post(`${API}/public/track`, {
            kind: 'profile', handle,
            ref: ref || sessionStorage.getItem('os_ref') || null,
          }).catch(() => {});
        }
        return axios.get(`${API}/reviews/user/${r.data.user_id}?limit=3`).catch(() => null);
      })
      .then((r) => { if (alive && r) setReviews(r.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="animate-spin h-8 w-8 border-3 border-[#7F77DD] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8] p-6 text-center" data-testid="public-profile-notfound">
        <p className="text-2xl font-bold text-gray-900">Profil introuvable</p>
        <p className="text-gray-500 mt-2">Ce lien ne correspond à aucun profil OpenSapiens.</p>
        <Link to="/" className="mt-6 text-[#7F77DD] font-semibold">Découvrir OpenSapiens →</Link>
      </div>
    );
  }

  const isOwner = user?.user_id === profile.user_id;
  const origin = window.location.origin;
  const ogUrl = `${origin}/api/@${profile.handle}`;
  const prettyUrl = `${origin}/@${profile.handle}`;
  const events = profile.upcoming_events || [];
  const posts = profile.recent_posts || [];
  const skills = (profile.skills || []).slice(0, 8);
  const appProfile = `/app/profile/${profile.user_id}`;
  const nextParam = encodeURIComponent(appProfile);
  const authUrl = (kind, target) => `/${kind}?redirect=${encodeURIComponent(target)}`;
  const firstName = (profile.display_name || '').split(' ')[0] || profile.display_name;

  // CTA : connecté → action directe dans l'app ; visiteur → inscription puis retour sur le profil.
  const goBook = () => navigate(user ? `${appProfile}?book=1` : authUrl('signup', `${appProfile}?book=1`));
  const goFollow = () => navigate(user ? `${appProfile}?follow=1` : authUrl('signup', `${appProfile}?follow=1`));
  const goMessage = () => navigate(user ? `/app/messages/${profile.user_id}` : authUrl('signup', `/app/messages/${profile.user_id}`));

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="public-expert-page">
      {/* Barre discrète : marque + accès */}
      <nav className="bg-white/80 backdrop-blur border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 h-12 flex items-center justify-between">
          <Link to="/" className="text-base font-bold bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 bg-clip-text text-transparent" data-testid="public-profile-brand">
            OpenSapiens
          </Link>
          {user ? (
            <button onClick={() => navigate(isOwner ? '/app/profile/' + user.user_id : appProfile)} className="text-sm font-medium text-[#7F77DD] hover:underline" data-testid="public-profile-open-app">
              Ouvrir dans l&apos;app →
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <Link to={`/login?redirect=${nextParam}`} className="text-sm text-gray-600 hover:text-gray-900" data-testid="public-profile-login">Se connecter</Link>
              <Link to={`/signup?redirect=${nextParam}`} className="text-sm font-semibold text-white px-3.5 py-1.5 rounded-lg hover:opacity-90" style={{ backgroundColor: ACCENT }} data-testid="public-profile-signup">
                Rejoindre
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 pt-12 pb-10">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <img
              src={profile.avatar}
              alt={profile.display_name}
              className="h-28 w-28 rounded-full object-cover border-4 border-white shadow-xl ring-1 ring-gray-100 flex-shrink-0"
              data-testid="public-profile-avatar"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight" data-testid="public-profile-name">
                  {profile.display_name}
                </h1>
                <button
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold text-gray-700 bg-gray-50 border border-gray-200 hover:border-[#7F77DD]/60 hover:text-[#7F77DD] transition-colors flex-shrink-0"
                  data-testid="public-profile-share-btn"
                  title="Partager ce profil"
                >
                  <Share2 className="h-4 w-4" /> <span className="hidden sm:inline">Partager</span>
                </button>
              </div>
              {profile.is_expert && (
                <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold uppercase tracking-wider text-[#7F77DD] bg-[#7F77DD]/10 px-2 py-0.5 rounded-full" data-testid="public-profile-expert-badge">
                  <Sparkles className="h-3 w-3" /> Expert
                </span>
              )}
              {profile.expert_pitch && (
                <p className="mt-2 text-[15px] text-gray-600 leading-relaxed" data-testid="public-profile-pitch">
                  {profile.expert_pitch}
                </p>
              )}
              {(profile.offer_tags || []).length > 0 && (
                <p className="mt-1.5 text-sm text-[#7F77DD] font-medium" data-testid="public-profile-tags">
                  {profile.offer_tags.join(' · ')}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-gray-500" data-testid="public-profile-stats">
                {profile.rating_avg > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Stars value={profile.rating_avg} />
                    <span className="font-semibold text-gray-700">{profile.rating_avg}</span>
                    <span>({profile.rating_count} avis)</span>
                  </span>
                )}
                {profile.sessions_completed > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarCheck className="h-4 w-4" /> {profile.sessions_completed} session{profile.sessions_completed > 1 ? 's' : ''} donnée{profile.sessions_completed > 1 ? 's' : ''}
                  </span>
                )}
                {profile.follower_count > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-4 w-4" /> {profile.follower_count} abonné{profile.follower_count > 1 ? 's' : ''}
                  </span>
                )}
                {profile.location && (
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {profile.location}</span>
                )}
              </div>

              {/* CTA principaux */}
              {!isOwner && (
                <div className="mt-5 flex flex-wrap items-center gap-2.5" data-testid="public-profile-cta">
                  {profile.is_expert && (
                    <PrimaryBtn onClick={goBook} testId="public-profile-book-btn">
                      <CalendarDays className="h-4 w-4" /> Réserver une session
                    </PrimaryBtn>
                  )}
                  {profile.is_expert ? (
                    <GhostBtn onClick={goFollow} testId="public-profile-follow-btn"><UserPlus className="h-4 w-4" /> Suivre</GhostBtn>
                  ) : (
                    <PrimaryBtn onClick={goFollow} testId="public-profile-follow-btn"><UserPlus className="h-4 w-4" /> Suivre {firstName}</PrimaryBtn>
                  )}
                  <GhostBtn onClick={goMessage} testId="public-profile-message-btn"><MessageCircle className="h-4 w-4" /> Message</GhostBtn>
                </div>
              )}
              {isOwner && (
                <div className="mt-5 flex flex-wrap items-center gap-2.5">
                  <PrimaryBtn onClick={() => setShareOpen(true)} testId="public-profile-owner-share"><Share2 className="h-4 w-4" /> Partager mon profil</PrimaryBtn>
                  <GhostBtn onClick={() => navigate(appProfile)} testId="public-profile-owner-edit">Modifier mon profil</GhostBtn>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 flex flex-col gap-12">
        {/* Bio */}
        {profile.bio && (
          <section data-testid="public-profile-bio">
            <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-line">{profile.bio}</p>
          </section>
        )}

        {/* Compétences */}
        {skills.length > 0 && (
          <section data-testid="public-profile-skills">
            <h2 className="text-lg font-bold text-gray-900 mb-3">{profile.is_expert ? 'Domaines d\u2019expertise' : 'Centres d\u2019intérêt'}</h2>
            <div className="flex flex-wrap gap-2">
              {skills.map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full text-sm bg-white border border-gray-200 text-gray-700">{s}</span>
              ))}
            </div>
          </section>
        )}

        {/* Prochaines sessions — experts uniquement */}
        {profile.is_expert && (
          <section data-testid="public-profile-events">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Prochaines sessions</h2>
            {events.length === 0 ? (
              <div className="py-8 px-6 text-center bg-white rounded-2xl border border-gray-100">
                <p className="text-gray-500 text-sm">Aucune session publique planifiée pour l&apos;instant.</p>
                <p className="text-gray-400 text-xs mt-1">Suis {firstName} pour être prévenu·e des prochaines dates.</p>
                <GhostBtn onClick={goFollow} testId="public-profile-events-follow" className="mt-4"><UserPlus className="h-4 w-4" /> Suivre {firstName}</GhostBtn>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((ev) => {
                  const { day, month } = fmtDay(ev.start_time);
                  const spotsLeft = Math.max(0, (ev.max_capacity || 0) - (ev.participants_count || 0));
                  const free = profile.force_free_sessions || ev.is_free || !(ev.price > 0);
                  return (
                    <button
                      key={ev.event_id}
                      onClick={() => navigate(`/@${profile.handle}/e/${ev.slug}`)}
                      className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 hover:border-[#7F77DD]/50 hover:shadow-md transition-all text-left group"
                      data-testid={`public-event-card-${ev.event_id}`}
                    >
                      <div className="flex flex-col items-center justify-center h-14 w-14 rounded-xl bg-[#7F77DD]/10 flex-shrink-0">
                        <span className="text-lg font-extrabold text-[#7F77DD] leading-none">{day}</span>
                        <span className="text-[11px] font-semibold text-[#7F77DD] uppercase">{month}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{ev.title}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <Clock className="h-3.5 w-3.5" /> {fmtTime(ev.start_time)} · {ev.duration_minutes} min
                          {spotsLeft > 0 && spotsLeft <= 5 && (
                            <span className="text-amber-600 font-medium">· {spotsLeft} place{spotsLeft > 1 ? 's' : ''} restante{spotsLeft > 1 ? 's' : ''}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${free ? 'bg-emerald-50 text-emerald-700' : 'bg-[#7F77DD]/10 text-[#7F77DD]'}`}>
                          {free ? 'Gratuit' : `${ev.price} ${ev.currency}`}
                        </span>
                        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-[#7F77DD] opacity-0 group-hover:opacity-100 transition-opacity">Réserver <ArrowRight className="h-3.5 w-3.5" /></span>
                        <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-[#7F77DD] transition-colors sm:hidden" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Dernières publications */}
        {posts.length > 0 && (
          <section data-testid="public-profile-posts">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Dernières publications</h2>
            <div className="space-y-3">
              {posts.map((p) => (
                <Link
                  key={p.post_id}
                  to={`/post/${p.post_id}`}
                  className="block p-4 bg-white rounded-2xl border border-gray-100 hover:border-[#7F77DD]/50 hover:shadow-md transition-all"
                  data-testid={`public-profile-post-${p.post_id}`}
                >
                  {p.content ? (
                    <p className="text-[15px] text-gray-800 leading-relaxed line-clamp-3 whitespace-pre-line">{p.content}</p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">{p.post_type === 'poll' ? 'Sondage' : 'Publication avec média'}</p>
                  )}
                  <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
                    <span>{fmtDate(p.created_at)}</span>
                    <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {p.like_count || 0}</span>
                    <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {p.comment_count || 0}</span>
                    <span className="ml-auto text-[#7F77DD] font-medium">Lire →</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Avis */}
        {reviews?.reviews?.length > 0 && (
          <section data-testid="public-profile-reviews">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Ce qu&apos;en disent les participants
              {reviews.total > 0 && <span className="text-gray-400 font-normal text-sm ml-2">({reviews.total} avis)</span>}
            </h2>
            <div className="space-y-3">
              {reviews.reviews.slice(0, 3).map((rv) => (
                <div key={rv.review_id} className="p-4 bg-white rounded-2xl border border-gray-100" data-testid={`public-review-${rv.review_id}`}>
                  <div className="flex items-center gap-2">
                    <Stars value={rv.rating} />
                    <span className="text-sm font-semibold text-gray-700">{rv.reviewer?.display_name || 'Participant'}</span>
                  </div>
                  {rv.comment && <p className="mt-2 text-sm text-gray-600 leading-relaxed">{rv.comment}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Bloc conversion */}
        {!isOwner && (
          <section className="rounded-3xl p-8 text-center text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #7F77DD 0%, #A855F7 60%, #EC4899 100%)' }} data-testid="public-profile-conversion">
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight">
              {profile.is_expert ? `Apprenez directement auprès de ${firstName}` : `Échangez avec ${firstName} sur OpenSapiens`}
            </h2>
            <p className="mt-2 text-white/85 text-sm sm:text-[15px] max-w-lg mx-auto">
              {profile.is_expert
                ? 'Réservez une session, posez vos questions et suivez ses prochaines dates. Gratuit pendant la beta.'
                : 'Suivez ses publications, rejoignez les mêmes communautés et réservez des sessions avec des experts. Gratuit pendant la beta.'}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {user ? (
                <button onClick={() => navigate(appProfile)} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-white text-[#7F77DD] hover:bg-gray-50 shadow" data-testid="public-profile-conversion-open">
                  Ouvrir dans OpenSapiens <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <>
                  <Link to={`/signup?redirect=${nextParam}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-white text-[#7F77DD] hover:bg-gray-50 shadow" data-testid="public-profile-conversion-signup">
                    Créer un compte gratuit <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to={`/login?redirect=${nextParam}`} className="inline-flex items-center px-5 py-3 rounded-xl text-sm font-semibold text-white border border-white/50 hover:bg-white/10" data-testid="public-profile-conversion-login">
                    J&apos;ai déjà un compte
                  </Link>
                </>
              )}
            </div>
          </section>
        )}
      </main>

      <footer className="py-8 text-center">
        <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 transition-colors" data-testid="public-profile-powered">
          Propulsé par <span className="font-semibold">OpenSapiens</span> — la plateforme suisse d&apos;échange de savoirs
        </Link>
      </footer>

      <SharePanel
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title={isOwner ? 'Partager mon profil' : `Partager le profil de ${firstName}`}
        shareUrl={ogUrl}
        prettyUrl={prettyUrl}
        defaultMessage={isOwner
          ? 'Découvrez mon profil et réservez une session avec moi 👇'
          : `Je te recommande le profil de ${profile.display_name} sur OpenSapiens 👇`}
      />
    </div>
  );
}
